// Chat is for quick, one-off questions about your data, so these read as
// lightweight questions — deliberately unlike AnimatedPlaceholder's multi-step
// workflow-building prompts used on the create-workflow surfaces.
//
// `connectors` are the connector identifiers a prompt touches: the first is the
// lead shown as the card logo, and having any of them makes the prompt relevant
// to that user. In `prompt`, *asterisks* wrap the product names to emphasize;
// they are stripped from the text inserted into the composer.
export type ChatExample = { connectors: string[]; prompt: string };

// Ordered roughly by how commonly the lead connector is used, so users with few
// or no connectors still see recognizable tools first.
const examples: ChatExample[] = [
  { connectors: ["slack"], prompt: "What did I miss in *Slack* today?" },
  {
    connectors: ["slack"],
    prompt: "Summarize the unread threads in my *Slack* #incidents channel.",
  },
  {
    connectors: ["slack"],
    prompt: "Who mentioned me in *Slack* this morning, and what do they need?",
  },
  {
    connectors: ["github"],
    prompt: "What *GitHub* pull requests are waiting on my review?",
  },
  {
    connectors: ["github"],
    prompt: "Summarize the *GitHub* issues assigned to me this week.",
  },
  {
    connectors: ["github"],
    prompt: "Which of my *GitHub* pull requests have failing checks?",
  },
  {
    connectors: ["jira"],
    prompt: "Which *Jira* issues are assigned to me right now?",
  },
  { connectors: ["jira"], prompt: "What’s blocking my current *Jira* sprint?" },
  {
    connectors: ["googleCalendar"],
    prompt: "What’s on my *Google Calendar* today, and what should I prep for?",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "When’s my next free hour on *Google Calendar*?",
  },
  {
    connectors: ["notion"],
    prompt: "Find our latest planning notes in *Notion*.",
  },
  {
    connectors: ["notion"],
    prompt: "What action items are still open in my *Notion* docs?",
  },
  {
    connectors: ["linear"],
    prompt: "What’s assigned to me in *Linear* this cycle?",
  },
  {
    connectors: ["linear"],
    prompt: "Summarize what my team shipped in *Linear* last week.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Which of my *Salesforce* deals are closing this month?",
  },
  {
    connectors: ["salesforce"],
    prompt: "Which *Salesforce* accounts haven’t I followed up with?",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Which *HubSpot* leads should I follow up with today?",
  },
  {
    connectors: ["zendesk"],
    prompt: "What are my highest-priority open *Zendesk* tickets?",
  },
  {
    connectors: ["zendesk"],
    prompt: "Summarize the *Zendesk* tickets breaching SLA today.",
  },
  {
    connectors: ["intercom"],
    prompt: "What are *Intercom* customers asking about most this week?",
  },
  {
    connectors: ["stripe"],
    prompt: "How much revenue did *Stripe* bring in this week?",
  },
  {
    connectors: ["stripe"],
    prompt: "Which *Stripe* subscriptions failed to renew recently?",
  },
  {
    connectors: ["datadog"],
    prompt: "Are there any active *Datadog* alerts I should know about?",
  },
  {
    connectors: ["pagerDuty"],
    prompt: "What *PagerDuty* incidents are open, and who’s on call?",
  },
  {
    connectors: ["sentry"],
    prompt: "What new *Sentry* errors have shown up since yesterday?",
  },
  {
    connectors: ["gitlab"],
    prompt: "Which *GitLab* merge requests are waiting on me?",
  },
  {
    connectors: ["figma"],
    prompt: "What changed in our *Figma* design files recently?",
  },
  {
    connectors: ["asana"],
    prompt: "Which *Asana* tasks are due for me this week?",
  },
  {
    connectors: ["confluence"],
    prompt: "Find our on-call runbook in *Confluence*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Summarize the latest numbers in my *Google Sheets* tracker.",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Find the latest draft in my *Google Docs* and summarize it.",
  },
  {
    connectors: ["shopify"],
    prompt: "How are today’s *Shopify* orders and sales tracking?",
  },
  {
    connectors: ["zoom"],
    prompt: "Summarize the action items from my last *Zoom* meeting.",
  },
  {
    connectors: ["airtable"],
    prompt: "What records changed in my *Airtable* base this week?",
  },
  {
    connectors: ["microsoftTeams"],
    prompt: "What did I miss in *Microsoft Teams* today?",
  },
  {
    connectors: ["serviceNow"],
    prompt: "What *ServiceNow* incidents are assigned to my team?",
  },
  {
    connectors: ["okta"],
    prompt: "Were there any suspicious *Okta* sign-ins overnight?",
  },
  {
    connectors: ["snowflake"],
    prompt: "Query *Snowflake* for this week’s signups by plan.",
  },
  {
    connectors: ["clickUp"],
    prompt: "What *ClickUp* tasks are due in my sprint this week?",
  },
  {
    connectors: ["mondayDotCom"],
    prompt: "What’s overdue on my *monday.com* boards?",
  },
  {
    connectors: ["trello"],
    prompt: "Which *Trello* cards are waiting in my review column?",
  },
  {
    connectors: ["github", "slack"],
    prompt:
      "Which *GitHub* pull requests are waiting on me, and did anyone nudge me about them in *Slack*?",
  },
  {
    connectors: ["jira", "github"],
    prompt:
      "Cross-reference my open *Jira* issues with recent *GitHub* commits.",
  },
  {
    connectors: ["salesforce", "zendesk"],
    prompt: "Which *Salesforce* accounts have open *Zendesk* tickets?",
  },
  {
    connectors: ["datadog", "pagerDuty"],
    prompt:
      "Correlate today’s *Datadog* alerts with the open *PagerDuty* incidents.",
  },
  {
    connectors: ["googleCalendar", "notion"],
    prompt:
      "Prep me for today’s *Google Calendar* meetings using notes from *Notion*.",
  },
  {
    connectors: ["slack"],
    prompt: "Draft a reply to my most recent *Slack* mention.",
  },
  {
    connectors: ["github"],
    prompt: "Draft a PR description for my current *GitHub* branch.",
  },
  {
    connectors: ["github"],
    prompt:
      "Write release notes from this week’s merged *GitHub* pull requests.",
  },
  {
    connectors: ["jira"],
    prompt: "Create a *Jira* ticket from a bug I’ll describe.",
  },
  {
    connectors: ["jira"],
    prompt: "Triage my unassigned *Jira* tickets and suggest priorities.",
  },
  {
    connectors: ["linear"],
    prompt: "Create a *Linear* issue and add it to my current cycle.",
  },
  {
    connectors: ["notion"],
    prompt: "Turn my rough notes into a tidy *Notion* doc.",
  },
  {
    connectors: ["googleCalendar"],
    prompt:
      "Find a free 30 minutes this week and draft a *Google Calendar* invite.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Draft a response to my oldest open *Zendesk* ticket.",
  },
  {
    connectors: ["intercom"],
    prompt: "Draft a reply to the latest *Intercom* conversation in my inbox.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Log a call summary on one of my *Salesforce* opportunities.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Draft a follow-up email to a *HubSpot* lead.",
  },
  {
    connectors: ["confluence"],
    prompt: "Draft a *Confluence* page from an outline I give you.",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Proofread my latest *Google Docs* draft and suggest edits.",
  },
  {
    connectors: ["googleSheets"],
    prompt:
      "Add this week’s totals as a summary row in my *Google Sheets* tracker.",
  },
  {
    connectors: ["stripe"],
    prompt: "Draft a recovery email for a failed *Stripe* payment.",
  },
  {
    connectors: ["asana"],
    prompt: "Break a project I describe into *Asana* tasks with due dates.",
  },
  {
    connectors: ["zoom"],
    prompt: "Turn my last *Zoom* meeting transcript into action items.",
  },
  {
    connectors: ["sentry", "linear"],
    prompt:
      "Triage the newest *Sentry* error and draft a *Linear* issue for it.",
  },
  {
    connectors: ["datadog", "pagerDuty"],
    prompt:
      "Draft an incident summary from the latest *Datadog* alert and open *PagerDuty* incident.",
  },
];

// Strip the *highlight* markers for the value inserted into the composer.
export function plainPrompt(prompt: string): string {
  return prompt.replace(/\*/g, "");
}

// Split a prompt into runs of plain and highlighted (product-name) text.
export function promptSegments(
  prompt: string
): { text: string; bold: boolean }[] {
  return prompt
    .split(/\*([^*]+)\*/)
    .map((text, index) => ({ text, bold: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0);
}

function seedOffset(seed: string, length: number): number {
  if (length === 0) return 0;
  let hash = 0;
  for (const char of seed) hash = (hash + char.charCodeAt(0)) | 0;
  return Math.abs(hash) % length;
}

function rotate<T>(items: readonly T[], seed: string): T[] {
  const offset = seedOffset(seed, items.length);
  return [...items.slice(offset), ...items.slice(0, offset)];
}

// The user’s own connectors first, then popular apps to top up — one card per
// lead connector, until `count` is reached. Each list is rotated by `seed` (the
// chat id) so every new chat surfaces a different slice of the bank while
// staying stable within that chat.
export function pickChatExamples(
  connectorIdentifiers: readonly string[],
  count: number,
  seed: string
): ChatExample[] {
  const userConnectors = new Set(connectorIdentifiers);
  const relevant = examples.filter((e) =>
    e.connectors.some((c) => userConnectors.has(c))
  );
  const relevantSet = new Set(relevant);
  const others = examples.filter((e) => !relevantSet.has(e));
  const pool = [...rotate(relevant, seed), ...rotate(others, seed)];

  const picked: ChatExample[] = [];
  const usedLeads = new Set<string>();
  for (const example of pool) {
    if (picked.length >= count) break;
    const lead = example.connectors[0];
    if (lead === undefined || usedLeads.has(lead)) continue;
    usedLeads.add(lead);
    picked.push(example);
  }
  return picked;
}
